import Phaser from 'phaser';
import { Player, Damageable } from '../entities/Player';
import { Enemy } from '../entities/Enemy';
import { EnemyFloater } from '../entities/EnemyFloater';
import { EnemyDropper, Orb } from '../entities/EnemyDropper';
import { EnemyHopper } from '../entities/EnemyHopper';
import { Portal } from '../entities/Portal';
import { Teleport } from '../entities/Teleport';
import { Projectile } from '../entities/Projectile';
import { Artefact } from '../entities/Artefact';
import { DecorItem } from '../entities/DecorItem';
import { InputState } from '../input/InputState';
import { KeyboardInput } from '../input/KeyboardInput';
import { TouchInput } from '../input/TouchInput';
import {
  SaveSystem,
  BiomeId,
  ArtefactId,
  ALL_ARTEFACTS,
} from '../systems/SaveSystem';
import { MapSystem } from '../systems/MapSystem';
import { LevelStorage } from '../systems/LevelData';
import { PORTAL_CONFIG, PLAYER_CONFIG } from '../config';

type ColorStops = Array<{ x: number; c: number }>;

export class GameScene extends Phaser.Scene {
  private biomeId: BiomeId = 'biome1';
  private worldWidth = 0;
  private worldHeight = 0;
  private terrain: Phaser.GameObjects.GameObject[] = [];
  private platformColorStops: ColorStops = [];

  private player!: Player;
  private enemies: Damageable[] = [];
  private hoppers: EnemyHopper[] = [];
  private enemyGroup!: Phaser.Physics.Arcade.Group;
  private projectileGroup!: Phaser.Physics.Arcade.Group;
  private orbs: Orb[] = [];
  private portal!: Portal;
  private teleports: Teleport[] = [];
  private mapSystem!: MapSystem;

  // Артефакты
  private artefacts: Artefact[] = [];
  private collectedArtefacts: Set<ArtefactId> = new Set();
  private artefactSlotIcons: Map<ArtefactId, Phaser.GameObjects.Image> = new Map();

  private inputState = new InputState();
  private keyboardInput!: KeyboardInput;
  private touchInput?: TouchInput;

  private portalRestTimer = 0;
  private portalUsedThisVisit = false;
  private isTeleporting = false;

  private hudLayer!: Phaser.GameObjects.Graphics;

  constructor() {
    super('GameScene');
  }

  init(data: { biomeId?: BiomeId }) {
    this.biomeId = data?.biomeId ?? 'biome1';
    // Reset state for scene restart
    this.enemies = [];
    this.hoppers = [];
    this.orbs = [];
    this.terrain = [];
    this.teleports = [];
    this.artefacts = [];
    this.collectedArtefacts = new Set();
    this.artefactSlotIcons = new Map();
    this.portalRestTimer = 0;
    this.portalUsedThisVisit = false;
    this.isTeleporting = false;
  }

  create() {
    const canvasW = this.scale.gameSize.width;
    const canvasH = this.scale.gameSize.height;

    // === Размеры мира + границы — биом-зависимые ===
    if (this.biomeId === 'biome1') {
      this.worldWidth = 10240;
      this.worldHeight = 2880;
      this.platformColorStops = [
        { x: 0, c: 0x1c2434 },
        { x: 3500, c: 0x232b3e },
        { x: 7000, c: 0x2a3346 },
        { x: 10240, c: 0x303a4e },
      ];
    } else {
      // Биом 2 — компактнее, замкнутая пещерная сеть
      this.worldWidth = 5120;
      this.worldHeight = 2400;
      // Палитра абисса: чёрный камень с тёплыми малиновыми вкраплениями
      this.platformColorStops = [
        { x: 0, c: 0x18121c },
        { x: 2500, c: 0x231424 },
        { x: 5120, c: 0x2c1a2c },
      ];
    }

    this.physics.world.setBounds(0, 0, this.worldWidth, this.worldHeight);
    this.cameras.main.setBounds(0, 0, this.worldWidth, this.worldHeight);

    this.mapSystem = new MapSystem(this, this.worldWidth, this.worldHeight);

    // === Контент биома ===
    if (this.biomeId === 'biome1') {
      this.buildBiome1Bg(canvasW, canvasH);
      this.buildBiome1Content();
    } else {
      this.buildBiome2Bg(canvasW, canvasH);
      this.buildBiome2Content();
    }

    // === Игрок ===
    const save = SaveSystem.load();
    if (save?.collectedArtefacts) {
      this.collectedArtefacts = new Set(save.collectedArtefacts);
    }
    const portalSpawn = this.portal.getRespawnPoint();
    this.player = new Player(this, portalSpawn.x, portalSpawn.y);
    this.player.setRespawnPoint(portalSpawn.x, portalSpawn.y);
    this.player.setDepth(10);
    if (save && save.biomeId === this.biomeId) {
      this.player.setState_(save.hp, save.soul);
    }

    this.portal.flashEmerge();
    this.player.playSpawnAnimation();

    this.physics.add.collider(this.player, this.terrain);

    // === Враги ===
    this.enemyGroup = this.physics.add.group();
    if (this.biomeId === 'biome1') {
      this.spawnBiome1Enemies();
    } else {
      this.spawnBiome2Enemies();
    }
    this.physics.add.overlap(this.player, this.enemyGroup, (_p, e) => {
      this.player.takeContactDamage(e as unknown as Damageable);
    });

    // === Снаряды героя ===
    this.projectileGroup = this.physics.add.group({ allowGravity: false });
    this.physics.add.overlap(this.projectileGroup, this.enemyGroup, (proj, enemy) => {
      const e = enemy as unknown as Damageable;
      const p = proj as Projectile;
      if (!p.isAlive() || !e.isAlive()) return;
      const knockbackX = (e.x > p.x ? 1 : -1) * 280;
      e.takeDamage(PLAYER_CONFIG.spellDamage, knockbackX);
      p.kill();
    });
    this.physics.add.overlap(this.projectileGroup, this.terrain, (proj) =>
      (proj as Projectile).kill(),
    );
    this.events.on('player-cast', (data: { x: number; y: number; dir: 1 | -1 }) => {
      const p = new Projectile(this, data.x, data.y, data.dir);
      p.setDepth(10);
      this.projectileGroup.add(p);
    });
    this.events.on('player-respawn', () => this.portal.flashEmerge());

    // === Орбы (снаряды Dropper) ===
    // Без Phaser physics body. Падают через ручной manualTick. Столкновения
    // с полом/игроком — через простую проверку координат в update().
    // Это полностью исключает любые шансы на зависание физики.
    this.events.on('dropper-emit', (data: { x: number; y: number }) => {
      const aliveCount = this.orbs.filter((o) => o.isAlive()).length;
      if (aliveCount >= 3) return;
      const o = new Orb(this, data.x, data.y);
      o.setDepth(10);
      this.orbs.push(o);
    });

    // === Артефакты ===
    if (this.biomeId === 'biome1') {
      this.placeBiome1Artefacts();
    } else {
      this.placeBiome2Artefacts();
    }
    for (const a of this.artefacts) {
      this.physics.add.overlap(this.player, a, () => this.handleArtefactPickup(a));
    }

    // === Телепорты — overlap-триггеры на смену биома ===
    for (const tp of this.teleports) {
      this.physics.add.overlap(this.player, tp, () => this.handleTeleport(tp));
    }

    // === Камера ===
    this.cameras.main.startFollow(this.player, true, 0.1, 0.1);
    this.cameras.main.setDeadzone(120, 80);

    // === Виньетка ===
    this.add
      .image(canvasW / 2, canvasH / 2, 'vignette')
      .setScrollFactor(0, 0)
      .setDepth(1500);

    // === Источники ввода ===
    this.keyboardInput = new KeyboardInput(this);
    const isTouchDevice = navigator.maxTouchPoints > 0 || 'ontouchstart' in window;
    if (isTouchDevice) {
      this.touchInput = new TouchInput(this, this.inputState);
    }

    this.input.keyboard?.on('keydown-M', () => this.mapSystem.toggle());
    // F1 → редактор уровней. Сохраняет текущий save (HP/soul/respawn/biome
    // уже сохранены при отдыхе у портала или подборе артефакта).
    this.input.keyboard?.on('keydown-F1', () => {
      this.scene.start('EditorScene', { biomeId: this.biomeId });
    });
    this.input.keyboard?.on('keydown-ESC', () => {
      if (this.mapSystem.isMapOpen()) this.mapSystem.close();
    });

    // === HUD ===
    this.hudLayer = this.add.graphics().setScrollFactor(0).setDepth(2000);
    this.setupArtefactHUDSlots(canvasW);

    if (!isTouchDevice) {
      this.add
        .text(
          24,
          76,
          '←/→ (D) — движение | Space/Z — прыжок | X/J — удар\n' +
            'A — лечить (Focus) | C — рывок | S — заклинание (Vengeful Spirit)\n' +
            'W/↑ + удар — взмах вверх | ↓ + удар (в воздухе) — Pogo\n' +
            'M — карта | Постоять у портала — сохранение и восстановление',
          {
            font: '12px monospace',
            color: '#aab',
            backgroundColor: '#00000055',
            padding: { x: 8, y: 6 },
          },
        )
        .setScrollFactor(0)
        .setDepth(2000);
    }
  }

  override update(_time: number, delta: number) {
    if (this.mapSystem.isMapOpen()) return;

    this.inputState.resetContinuous();
    this.keyboardInput.update(this.inputState);
    this.touchInput?.update(this.inputState);

    this.player.tick(delta, this.inputState);
    this.player.checkAttackOverlap(this.enemies);
    this.enemies = this.enemies.filter((e) => e.isAlive());

    this.handlePortalRest(delta);
    this.mapSystem.updatePlayer(this.player.x, this.player.y);

    // Hoppers — каждый кадр оценивают, прыгать ли в сторону героя
    const cam = this.cameras.main;
    const camLeft = cam.scrollX;
    const camRight = cam.scrollX + cam.width;
    for (const h of this.hoppers) {
      if (!h.isAlive()) continue;
      const onScreen = h.x >= camLeft - 100 && h.x <= camRight + 100;
      h.considerHop(this.player.x, onScreen);
    }
    this.hoppers = this.hoppers.filter((h) => h.isAlive());

    // Орбы — ручной tick + ручная коллизия (без Phaser physics body).
    const floorTop = this.worldHeight - 32;
    for (const o of this.orbs) {
      if (!o.isAlive()) continue;
      o.manualTick(delta);

      // Касание игрока — взрыв + урон
      const dx = o.x - this.player.x;
      const dy = o.y - this.player.y;
      if (Math.abs(dx) < 50 && Math.abs(dy) < 60) {
        o.explode();
        this.player.takeContactDamage({
          x: o.x,
          y: o.y,
          active: true,
          isAlive: () => true,
          takeDamage: () => {},
        });
        continue;
      }

      // Касание пола — лопается без вреда
      if (o.y >= floorTop - 8) {
        o.popHarmless();
      }
    }
    this.orbs = this.orbs.filter((o) => o.isAlive());

    this.updateHUD();

    this.inputState.resetOneShot();
  }

  // ------------------------------------------------------------------
  // ОБЩИЕ ХЕЛПЕРЫ ОТРИСОВКИ ЗЕМЛИ И ПЛАТФОРМ
  // ------------------------------------------------------------------

  private addBlock(cx: number, cy: number, w: number, h: number, key: string) {
    const t = this.add.tileSprite(cx, cy, w, h, key);
    t.setTileScale(0.2, 0.2);
    t.setDepth(0);
    this.physics.add.existing(t, true);
    this.terrain.push(t);
    return t;
  }

  private static lerpColor(a: number, b: number, t: number): number {
    const ar = (a >> 16) & 0xff;
    const ag = (a >> 8) & 0xff;
    const ab = a & 0xff;
    const br = (b >> 16) & 0xff;
    const bg = (b >> 8) & 0xff;
    const bb = b & 0xff;
    return (
      (Math.round(ar + (br - ar) * t) << 16) |
      (Math.round(ag + (bg - ag) * t) << 8) |
      Math.round(ab + (bb - ab) * t)
    );
  }

  private static adjustBrightness(color: number, amount: number): number {
    const r = (color >> 16) & 0xff;
    const g = (color >> 8) & 0xff;
    const b = color & 0xff;
    const clamp = (v: number) => Math.max(0, Math.min(255, v));
    return (clamp(r + amount) << 16) | (clamp(g + amount) << 8) | clamp(b + amount);
  }

  private static colorAtStops(wx: number, stops: ColorStops): number {
    if (wx <= stops[0].x) return stops[0].c;
    if (wx >= stops[stops.length - 1].x) return stops[stops.length - 1].c;
    for (let i = 0; i < stops.length - 1; i++) {
      if (wx >= stops[i].x && wx <= stops[i + 1].x) {
        const t = (wx - stops[i].x) / (stops[i + 1].x - stops[i].x);
        return GameScene.lerpColor(stops[i].c, stops[i + 1].c, t);
      }
    }
    return stops[0].c;
  }

  /**
   * Платформа: 7 вариантов формы + плавный цветовой градиент по X.
   * Верх условно плоский (3px кромка для тонких h≤20, 5px для утолщённых),
   * боковые/нижние грани сильно варьируются — выглядит как «висящий фрагмент».
   * Физическое тело h определяется параметром.
   */
  private addPlatform(cx: number, cy: number, w: number, h: number) {
    const stops = this.platformColorStops;
    const x = cx - w / 2;
    const y = cy - h / 2;
    const g = this.add.graphics();
    g.setDepth(0);

    const topH = h >= 24 ? 5 : 3;

    g.fillStyle(0x000000, 0.45);
    g.fillRect(x - 2, y + h, w + 4, 4);
    g.fillStyle(0x000000, 0.2);
    g.fillRect(x - 5, y + h, w + 10, 7);

    const seed = Math.floor(Math.abs(cx * 7 + cy * 13));
    const jitter = (seed % 17) - 8;
    const baseColorRaw = GameScene.colorAtStops(cx, stops);
    const baseColor = GameScene.adjustBrightness(baseColorRaw, jitter);
    const topColor = GameScene.adjustBrightness(baseColor, 18);
    const highlight = GameScene.adjustBrightness(baseColor, 36);
    const seamColor = GameScene.adjustBrightness(baseColor, -16);

    const drawTop = (highlightInset: number) => {
      g.fillStyle(topColor, 1);
      g.fillRect(x, y, w, topH);
      g.fillStyle(highlight, 0.55);
      g.fillRect(x + highlightInset, y, w - highlightInset * 2, 1);
    };

    const variant = seed % 7;
    switch (variant) {
      case 0: {
        g.fillStyle(baseColor, 1);
        g.fillPoints(
          [
            { x: x, y: y + topH },
            { x: x + w, y: y + topH },
            { x: x + w - 4, y: y + h - 2 },
            { x: x + w - 14, y: y + h + 6 },
            { x: x + 14, y: y + h + 6 },
            { x: x + 4, y: y + h - 2 },
          ],
          true,
          true,
        );
        drawTop(4);
        break;
      }
      case 1: {
        g.fillStyle(baseColor, 1);
        g.fillPoints(
          [
            { x: x, y: y + topH },
            { x: x + w, y: y + topH },
            { x: x + w - 2, y: y + h - 1 },
            { x: x + w - 10, y: y + h + 3 },
            { x: x + Math.floor(w * 0.55), y: y + h - 2 },
            { x: x + Math.floor(w * 0.35), y: y + h + 8 },
            { x: x + 12, y: y + h + 9 },
            { x: x, y: y + h - 3 },
          ],
          true,
          true,
        );
        drawTop(4);
        break;
      }
      case 2: {
        g.fillStyle(baseColor, 1);
        g.fillRoundedRect(x, y + topH, w, h - topH - 1, { tl: 0, tr: 0, bl: 6, br: 6 });
        const dripPositions = [0.22, 0.55, 0.78];
        for (let i = 0; i < dripPositions.length; i++) {
          const dx = x + w * dripPositions[i];
          const dRadius = 4 + ((i + seed) % 3);
          const dDepth = 4 + ((seed + i * 5) % 6);
          g.fillStyle(baseColor, 1);
          g.fillEllipse(dx, y + h + dDepth - dRadius, dRadius * 1.6, dRadius * 2);
        }
        drawTop(5);
        break;
      }
      case 3: {
        const stones = Math.max(3, Math.floor(w / 32));
        const stoneW = w / stones;
        for (let i = 0; i < stones; i++) {
          const sx = x + i * stoneW;
          const variation = ((i * 7 + seed) % 5) - 2;
          const dropY = (seed + i * 3) % 4;
          const sw = stoneW - 2 + ((seed + i) % 3 === 0 ? 4 : 0);
          const sh = h - 2 + dropY * 2;
          g.fillStyle(GameScene.adjustBrightness(baseColor, variation * 5), 1);
          g.fillRoundedRect(sx + 1, y + 2, sw, sh, 4);
          g.fillStyle(GameScene.adjustBrightness(topColor, variation * 5), 1);
          g.fillRect(sx + 2, y + 1, stoneW - 4, Math.max(2, topH - 1));
        }
        break;
      }
      case 4: {
        g.fillStyle(baseColor, 1);
        g.fillRect(x, y + topH, w, h - topH);
        g.fillStyle(GameScene.adjustBrightness(baseColor, -10), 1);
        g.fillRoundedRect(x + 6, y + h, w - 12, 4, { tl: 0, tr: 0, bl: 3, br: 3 });
        g.fillStyle(GameScene.adjustBrightness(baseColor, -18), 1);
        g.fillRoundedRect(x + 18, y + h + 4, w - 36, 3, 2);
        drawTop(4);
        g.fillStyle(seamColor, 0.6);
        g.fillRect(x + 4, y + h - 5, w - 8, 1);
        break;
      }
      case 5: {
        const segments = Math.max(5, Math.floor(w / 16));
        const segW = w / segments;
        const points: Array<{ x: number; y: number }> = [];
        points.push({ x: x + 2, y: y + topH });
        points.push({ x: x + w - 2, y: y + topH });
        points.push({ x: x + w, y: y + h - 3 });
        for (let i = segments; i >= 0; i--) {
          const sx = x + i * segW;
          const ridge = (i + seed) % 4 === 0 ? 8 : (i + seed) % 3 === 0 ? 4 : 0;
          points.push({ x: sx, y: y + h + ridge });
        }
        points.push({ x: x, y: y + h - 4 });
        g.fillStyle(baseColor, 1);
        g.fillPoints(points, true, true);
        drawTop(4);
        break;
      }
      case 6: {
        g.fillStyle(baseColor, 1);
        g.fillRoundedRect(x, y + topH, w, h - topH - 1, { tl: 1, tr: 1, bl: 8, br: 8 });
        const stemW = Math.min(60, Math.max(20, w * 0.35));
        const stemX = x + (w - stemW) / 2;
        g.fillStyle(GameScene.adjustBrightness(baseColor, -8), 1);
        g.fillRoundedRect(stemX, y + h - 1, stemW, 9, { tl: 0, tr: 0, bl: 4, br: 4 });
        drawTop(5);
        break;
      }
    }

    const body = this.add.rectangle(cx, cy, w, h);
    body.setVisible(false);
    this.physics.add.existing(body, true);
    this.terrain.push(body);
    this.mapSystem.registerPlatform(cx, cy, w, h);
    return body;
  }

  // ------------------------------------------------------------------
  // BIOME 1 — открытый мир с парящими платформами
  // ------------------------------------------------------------------

  private buildBiome1Bg(canvasW: number, canvasH: number) {
    const bgScrollFactorX = 0.5;
    const cameraMaxScrollX = this.worldWidth - canvasW;
    const bgTotalWidth = canvasW + cameraMaxScrollX * bgScrollFactorX;
    const bgKeys = [
      'biome1-bg',
      'biome1-bg-cathedral',
      'biome1-bg-temple',
      'biome1-bg-crypt',
    ];
    const bgN = bgKeys.length;
    const bgPieceW = bgTotalWidth / 2.5;
    const bgSpacing = (bgTotalWidth - bgPieceW) / (bgN - 1);

    for (let i = 0; i < bgN; i++) {
      const bgX = bgPieceW / 2 + i * bgSpacing;
      const bg = this.add.image(bgX, canvasH / 2, bgKeys[i]);
      bg.setScale(bgPieceW / bg.width);
      bg.setScrollFactor(bgScrollFactorX, 0);
      bg.setDepth(-100);

      const fadeMask = this.add.image(bgX, canvasH / 2, 'bg-fade-mask').setVisible(false);
      fadeMask.setScale(bg.displayWidth / fadeMask.width, bg.displayHeight / fadeMask.height);
      fadeMask.setScrollFactor(bgScrollFactorX, 0);
      bg.setMask(fadeMask.createBitmapMask());
    }
  }

  private buildBiome1Content() {
    const W = this.worldWidth;
    const H = this.worldHeight;

    const customLevel = LevelStorage.load(this.biomeId);
    const useCustomGeometry =
      customLevel && (customLevel.platforms.length > 0 || customLevel.blocks.length > 0);

    if (useCustomGeometry && customLevel) {
      for (const b of customLevel.blocks) this.addBlock(b.cx, b.cy, b.w, b.h, b.texture);
      for (const p of customLevel.platforms) this.addPlatform(p.cx, p.cy, p.w, p.h);
    } else {
    // Пол / стены
    this.addBlock(W / 2, H - 16, W, 32, 'biome1-wall');
    this.addBlock(16, (200 + H - 32) / 2, 32, H - 232, 'biome1-wall');
    this.addBlock(W - 16, (200 + H - 32) / 2, 32, H - 232, 'biome1-wall');

    // Платформы (7 групп). Толщина h=32 (вдвое толще исходных h=16).
    this.addPlatform(500, 2768, 300, 32);
    this.addPlatform(900, 2700, 200, 32);
    this.addPlatform(1200, 2620, 180, 32);
    this.addPlatform(1620, 2620, 200, 32);
    this.addPlatform(1980, 2540, 180, 32);
    this.addPlatform(2270, 2460, 180, 32);
    this.addPlatform(2455, 2640, 100, 32);

    this.addPlatform(2500, 2300, 140, 32);
    this.addPlatform(2150, 2150, 140, 32);
    this.addPlatform(2500, 2000, 140, 32);
    this.addPlatform(2150, 1850, 140, 32);
    this.addPlatform(2500, 1700, 140, 32);
    this.addPlatform(2150, 1550, 140, 32);
    this.addPlatform(2500, 1400, 140, 32);

    this.addPlatform(2900, 1380, 200, 32);
    this.addPlatform(3300, 1380, 200, 32);
    this.addPlatform(3700, 1320, 200, 32);
    this.addPlatform(4100, 1320, 200, 32);
    this.addPlatform(4500, 1260, 200, 32);

    this.addPlatform(4900, 1110, 180, 32);
    this.addPlatform(5200, 960, 180, 32);
    this.addPlatform(5500, 810, 180, 32);
    this.addPlatform(5800, 660, 180, 32);

    this.addPlatform(6200, 510, 220, 32);
    this.addPlatform(6500, 380, 200, 32);
    this.addPlatform(6800, 250, 200, 32);
    this.addPlatform(7200, 250, 220, 32);
    this.addPlatform(7700, 320, 220, 32);
    this.addPlatform(8200, 400, 220, 32);
    this.addPlatform(8800, 450, 250, 32);

    // === Маршрут к артефакту a1 (mid-east, ниже основного траверса) ===
    // От existing e3 (3700,1320) → спуск по 3 платформам к a1 на (4000,1866).
    this.addPlatform(3900, 1480, 180, 32); // ar1
    this.addPlatform(4100, 1660, 180, 32); // ar2
    this.addPlatform(4000, 1900, 220, 32); // ar3 — площадка артефакта

    // === Маршрут к артефакту a2 (далеко-вверху-справа) ===
    // От top7 (8800,450) → подъём по 3 платформам в правый верхний угол.
    this.addPlatform(9100, 380, 200, 32);  // br1
    this.addPlatform(9400, 320, 200, 32);  // br2
    this.addPlatform(9700, 280, 220, 32);  // br3 — площадка артефакта
    } // end else (hardcoded geometry)

    // Декорации
    const DECOR_BOTTOM_Y = H + 15;
    const placeDecor = (x: number, key: string, scale: number) => {
      this.add
        .image(x, DECOR_BOTTOM_Y, key)
        .setOrigin(0.5, 1)
        .setScale(scale)
        .setAlpha(0.55)
        .setScrollFactor(0.85, 1.0)
        .setDepth(-50);
    };
    placeDecor(450, 'biome1-column', 0.6);
    placeDecor(1100, 'biome1-rubble', 0.5);
    placeDecor(1900, 'biome1-column', 0.5);
    placeDecor(3500, 'biome1-rubble', 0.55);
    placeDecor(4800, 'biome1-column', 0.55);
    placeDecor(6200, 'biome1-rubble', 0.5);
    placeDecor(7400, 'biome1-column', 0.6);
    placeDecor(8700, 'biome1-rubble', 0.5);
    placeDecor(9700, 'biome1-column', 0.55);

    // === Портал-зеркало (точка респауна) ===
    const portalFloorY = H - 32;
    this.portal = new Portal(this, 200, portalFloorY);
    this.portal.setDepth(1);

    // === Телепорт в биом 2 — на финальной верхней платформе ===
    const tp1to2 = new Teleport(this, 8800, 434, 'biome2');
    tp1to2.setDepth(1);
    this.teleports.push(tp1to2);

    // === Декор 4-го плана ===
    this.placeBiome1Decor(H);
  }

  private placeBiome1Decor(H: number) {
    const customLevel = LevelStorage.load(this.biomeId);
    if (customLevel && customLevel.decorations.length > 0) {
      for (const d of customLevel.decorations) {
        this.addDecor(d.x, d.y, d.key, d.scale, d.layer);
      }
      return;
    }

    const floorTop = H - 32; // 2848

    // 9 вариантов обломков для миксования в груды
    const RUBBLE_VARIANTS = [
      'decor-b1-rubble',
      'decor-b1-rubble-2',
      'decor-b1-rubble-3',
      'decor-b1-rubble-4',
      'decor-b1-rubble-5',
      'decor-b1-rubble-6',
      'decor-b1-rubble-7',
      'decor-b1-rubble-8',
      'decor-b1-rubble-9',
    ];
    // 5 вариантов надгробий для групповых композиций
    const TOMB_VARIANTS = [
      'decor-b1-tombstone',
      'decor-b1-tombstone-2',
      'decor-b1-tombstone-3',
      'decor-b1-tombstone-4',
      'decor-b1-tombstone-5',
    ];

    // Стартовая зона — большая груда обломков у портала
    this.addDecorCluster(RUBBLE_VARIANTS, 0.09, 600, floorTop, [
      { dx: -40, v: 5, s: 1 },
      { dx: -15, v: 0, s: 2 },
      { dx: 12, v: 8, s: 0 },
      { dx: 35, v: 3, s: 1 },
    ]);
    this.addDecorCluster(RUBBLE_VARIANTS, 0.10, 900, floorTop, [
      { dx: -30, v: 6, s: 0 },
      { dx: -5, v: 1, s: 2 },
      { dx: 24, v: 7, s: 1 },
    ]);

    // Кладбище у платформ p1-p7 — группа надгробий + груда руин
    this.addDecorCluster(TOMB_VARIANTS, 0.10, 1300, floorTop, [
      { dx: -45, v: 0, s: 0 },
      { dx: -18, v: 3, s: 2 },
      { dx: 12, v: 1, s: 1 },
      { dx: 42, v: 4, s: 0 },
    ]);
    this.addDecorCluster(RUBBLE_VARIANTS, 0.09, 1560, floorTop, [
      { dx: -28, v: 4, s: 1 },
      { dx: 0, v: 2, s: 2 },
      { dx: 28, v: 8, s: 0 },
    ]);
    this.addDecor(1850, floorTop, 'decor-b1-stone-block', 0.12);
    this.addDecorCluster(RUBBLE_VARIANTS, 0.10, 2150, floorTop, [
      { dx: -28, v: 5, s: 0 },
      { dx: 0, v: 0, s: 2 },
      { dx: 26, v: 6, s: 1 },
      { dx: 50, v: 3, s: 0 },
    ]);

    // Под зигзаг-башней
    this.addDecorCluster(RUBBLE_VARIANTS, 0.09, 2700, floorTop, [
      { dx: -32, v: 7, s: 1 },
      { dx: -8, v: 1, s: 2 },
      { dx: 18, v: 8, s: 0 },
      { dx: 42, v: 2, s: 1 },
    ]);
    this.addDecorCluster(TOMB_VARIANTS, 0.09, 3000, floorTop, [
      { dx: -35, v: 2, s: 0 },
      { dx: -8, v: 4, s: 2 },
      { dx: 22, v: 0, s: 1 },
    ]);
    this.addDecor(3300, floorTop, 'decor-b1-stone-block', 0.13);
    this.addDecorCluster(RUBBLE_VARIANTS, 0.10, 3650, floorTop, [
      { dx: -30, v: 3, s: 0 },
      { dx: -2, v: 7, s: 2 },
      { dx: 22, v: 4, s: 1 },
      { dx: 48, v: 0, s: 0 },
    ]);

    // Под восточным траверсом — баннер + надгробия + руины
    this.addDecor(4100, floorTop, 'decor-b1-banner', 0.16);
    this.addDecorCluster(TOMB_VARIANTS, 0.10, 4400, floorTop, [
      { dx: -30, v: 1, s: 1 },
      { dx: 0, v: 3, s: 0 },
      { dx: 30, v: 4, s: 2 },
    ]);
    this.addDecorCluster(RUBBLE_VARIANTS, 0.09, 4750, floorTop, [
      { dx: -25, v: 8, s: 1 },
      { dx: 0, v: 5, s: 2 },
      { dx: 26, v: 1, s: 0 },
    ]);

    // Большое кладбище в средней секции
    this.addDecorCluster(TOMB_VARIANTS, 0.10, 5400, floorTop, [
      { dx: -55, v: 0, s: 1 },
      { dx: -25, v: 2, s: 0 },
      { dx: 5, v: 3, s: 2 },
      { dx: 35, v: 4, s: 1 },
      { dx: 62, v: 1, s: 0 },
    ]);
    this.addDecorCluster(RUBBLE_VARIANTS, 0.10, 5750, floorTop, [
      { dx: -28, v: 6, s: 0 },
      { dx: 0, v: 0, s: 2 },
      { dx: 28, v: 4, s: 1 },
    ]);
    this.addDecor(5980, floorTop, 'decor-b1-stone-block', 0.13);

    // Под пиком — больше руин
    this.addDecorCluster(RUBBLE_VARIANTS, 0.09, 6400, floorTop, [
      { dx: -30, v: 2, s: 1 },
      { dx: -5, v: 7, s: 0 },
      { dx: 22, v: 5, s: 2 },
      { dx: 48, v: 1, s: 1 },
    ]);
    this.addDecorCluster(TOMB_VARIANTS, 0.09, 6750, floorTop, [
      { dx: -28, v: 3, s: 1 },
      { dx: -2, v: 0, s: 0 },
      { dx: 25, v: 4, s: 2 },
    ]);
    this.addDecorCluster(RUBBLE_VARIANTS, 0.10, 7100, floorTop, [
      { dx: -32, v: 8, s: 2 },
      { dx: -5, v: 3, s: 1 },
      { dx: 22, v: 0, s: 0 },
      { dx: 48, v: 6, s: 1 },
    ]);
    this.addDecor(7480, floorTop, 'decor-b1-stone-block', 0.12);
    this.addDecorCluster(TOMB_VARIANTS, 0.10, 7800, floorTop, [
      { dx: -32, v: 1, s: 1 },
      { dx: 0, v: 2, s: 2 },
      { dx: 32, v: 4, s: 0 },
    ]);
    this.addDecorCluster(RUBBLE_VARIANTS, 0.09, 8200, floorTop, [
      { dx: -25, v: 4, s: 0 },
      { dx: 0, v: 7, s: 2 },
      { dx: 26, v: 2, s: 1 },
    ]);

    // Финальная атмосфера у дальней стены
    this.addDecorCluster(RUBBLE_VARIANTS, 0.10, 9050, floorTop, [
      { dx: -30, v: 5, s: 1 },
      { dx: 0, v: 8, s: 2 },
      { dx: 28, v: 3, s: 0 },
      { dx: 56, v: 1, s: 1 },
    ]);
    this.addDecorCluster(TOMB_VARIANTS, 0.10, 9450, floorTop, [
      { dx: -35, v: 3, s: 1 },
      { dx: -8, v: 2, s: 0 },
      { dx: 22, v: 0, s: 2 },
    ]);
    this.addDecor(9700, floorTop, 'decor-b1-lantern', 0.13);
  }

  private spawnBiome1Enemies() {
    // Crawler-жуки
    this.spawnEnemy(500, 2748, 400, 600);
    this.spawnEnemy(1620, 2600, 1540, 1700);
    this.spawnEnemy(3500, 1368, 3300, 3700);

    // Floater — Xonix-стиль: 45° (vx = vy = 64 → ~90 px/s), отскок от
    // террейна (платформы, стены, пол) и world bounds.
    // Стартует в стартовой зоне — игрок встретит сразу
    this.addFloater(800, 2400, 64, 64);
    // Второй — над восточной частью
    this.addFloater(4500, 1500, -64, 64);

    // Dropper — плавает под потолком, сбрасывает орбы
    this.addDropper(5500, 600, 4900, 6200);

    // Hopper — лягушка-жук
    this.addHopper(2000, 0, 1700, 2400);
    this.addHopper(5500, 0, 5000, 6200);
    this.addHopper(8500, 0, 8000, 9200);
  }

  // ------------------------------------------------------------------
  // BIOME 2 — пещеры с коридорами и камерами
  // ------------------------------------------------------------------

  private buildBiome2Bg(canvasW: number, canvasH: number) {
    const bgScrollFactorX = 0.5;
    const cameraMaxScrollX = this.worldWidth - canvasW;
    const bgTotalWidth = canvasW + cameraMaxScrollX * bgScrollFactorX;
    const bgKeys = ['biome2-bg-crystals', 'biome2-bg-flora', 'biome2-bg-lava'];
    const bgN = bgKeys.length;
    const bgPieceW = bgTotalWidth / 2.0; // больше перекрытия
    const bgSpacing = (bgTotalWidth - bgPieceW) / (bgN - 1);

    for (let i = 0; i < bgN; i++) {
      const bgX = bgPieceW / 2 + i * bgSpacing;
      const bg = this.add.image(bgX, canvasH / 2, bgKeys[i]);
      bg.setScale(bgPieceW / bg.width);
      bg.setScrollFactor(bgScrollFactorX, 0);
      bg.setDepth(-100);

      const fadeMask = this.add.image(bgX, canvasH / 2, 'bg-fade-mask').setVisible(false);
      fadeMask.setScale(bg.displayWidth / fadeMask.width, bg.displayHeight / fadeMask.height);
      fadeMask.setScrollFactor(bgScrollFactorX, 0);
      bg.setMask(fadeMask.createBitmapMask());
    }
  }

  private buildBiome2Content() {
    const W = this.worldWidth; // 5120
    const H = this.worldHeight; // 2400
    const WALL = 'biome2-wall';

    const customLevel = LevelStorage.load(this.biomeId);
    const useCustomGeometry =
      customLevel && (customLevel.platforms.length > 0 || customLevel.blocks.length > 0);

    if (useCustomGeometry && customLevel) {
      for (const b of customLevel.blocks) this.addBlock(b.cx, b.cy, b.w, b.h, b.texture);
      for (const p of customLevel.platforms) this.addPlatform(p.cx, p.cy, p.w, p.h);
    } else {
    // === Внешняя оболочка пещеры ===
    // Пол по всей ширине
    this.addBlock(W / 2, H - 16, W, 32, WALL);
    // Потолок по всей ширине
    this.addBlock(W / 2, 16, W, 32, WALL);
    // Левая и правая внешние стены (от потолка до пола)
    this.addBlock(16, H / 2, 32, H - 64, WALL);
    this.addBlock(W - 16, H / 2, 32, H - 64, WALL);

    // === Внутренние стены, формирующие чемберы и проходы ===
    // Разделитель 1 (Чамбер 1 ↔ Чамбер 2). Gap высотой 280px на средней высоте,
    // достаточно широкий чтобы герой (104 высотой) свободно прошёл прыжком.
    // x=2200, gap y=1780..2060.
    this.addBlock(2200, 906, 64, 1748, WALL); // верхняя y=32..1780
    this.addBlock(2200, 2214, 64, 308, WALL); // нижняя y=2060..2368

    // Разделитель 2 (Чамбер 2 ↔ Чамбер 3). Gap высотой 280px выше — проход
    // через арку шахты. x=3700, gap y=1100..1380.
    this.addBlock(3700, 566, 64, 1068, WALL); // верхняя y=32..1100
    this.addBlock(3700, 1874, 64, 988, WALL); // нижняя y=1380..2368

    // === Платформы Чамбер 1 (x=32..2168) ===
    // Лестница от пола вверх к gap'у разделителя 1 (y=1780..2060).
    this.addPlatform(400, 2200, 200, 32);
    this.addPlatform(800, 2060, 200, 32);
    this.addPlatform(1200, 1960, 220, 32);
    this.addPlatform(1600, 1900, 220, 32);
    // Прыжковая площадка у самого края gap'а — top y=1934, центр игрока 1882
    // (head y=1830, ноги 1934 — полностью в gap 1780..2060)
    this.addPlatform(2050, 1950, 180, 32);

    // === Платформы Чамбер 2 (x=2232..3668) ===
    // Приземление после gap'а 1, потом подъём к gap'у 2 (y=1100..1380)
    this.addPlatform(2380, 1960, 220, 32);  // продолжение gap-подхода
    this.addPlatform(2700, 1820, 200, 32);
    this.addPlatform(3000, 1640, 200, 32);
    this.addPlatform(3250, 1460, 200, 32);
    // Прыжковая площадка у gap'а 2 — top y=1310, head 1206 (gap top=1100)
    this.addPlatform(3500, 1326, 200, 32);

    // === Платформы Чамбер 3 (x=3732..5088) ===
    // Приземление после gap'а 2 (player падает с верха разделителя y=1380)
    this.addPlatform(3870, 1460, 200, 32); // top 1444, ловит падающего из gap'а
    this.addPlatform(4150, 1640, 200, 32);
    this.addPlatform(4400, 1840, 200, 32);
    this.addPlatform(4650, 2050, 220, 32);
    this.addPlatform(4900, 2200, 180, 32); // финальная посадка перед телепортом

    // === Маршрут к артефакту a3 (вверх в Чамбере 2) ===
    // От p10 (3500,1326) → 2 платформы налево-вверх, артефакт на верхней.
    this.addPlatform(3100, 1130, 180, 32); // sa1
    this.addPlatform(2900, 940, 220, 32);  // sa2 — площадка артефакта

    // === Маршрут к артефакту a4 (вверх в Чамбере 3) ===
    // От p11 (3870,1460) → 2 платформы вверх-направо, артефакт на верхней.
    this.addPlatform(3850, 1240, 200, 32); // sb1
    this.addPlatform(4050, 1040, 220, 32); // sb2 — площадка артефакта
    } // end else (hardcoded geometry)

    // === Портал-зеркало в Чамбере 1 ===
    const portalFloorY2 = H - 32;
    this.portal = new Portal(this, 240, portalFloorY2);
    this.portal.setDepth(1);

    // === Телепорт обратно в биом 1 ===
    const tp2to1 = new Teleport(this, W - 200, portalFloorY2, 'biome1');
    tp2to1.setDepth(1);
    this.teleports.push(tp2to1);

    // === Декор 4-го плана ===
    this.placeBiome2Decor(H);
  }

  private placeBiome2Decor(H: number) {
    const customLevel = LevelStorage.load(this.biomeId);
    if (customLevel && customLevel.decorations.length > 0) {
      for (const d of customLevel.decorations) {
        this.addDecor(d.x, d.y, d.key, d.scale, d.layer);
      }
      return;
    }

    const floor2 = H - 32; // 2368

    // 4 варианта грибов и кристаллов для миксования в полянки
    const MUSHROOM_VARIANTS = [
      'decor-b2-mushroom',
      'decor-b2-mushroom-2',
      'decor-b2-mushroom-3',
      'decor-b2-mushroom-4',
    ];
    const CRYSTAL_VARIANTS = [
      'decor-b2-crystal-cluster',
      'decor-b2-crystal-cluster-2',
      'decor-b2-crystal-cluster-3',
      'decor-b2-crystal-cluster-4',
    ];

    // Чамбер 1 — большая «полянка грибов» + россыпь кристаллов
    this.addDecorCluster(MUSHROOM_VARIANTS, 0.10, 480, floor2, [
      { dx: -32, v: 0, s: 1 },
      { dx: -8, v: 2, s: 0 },
      { dx: 18, v: 1, s: 2 },
      { dx: 42, v: 3, s: 1 },
    ]);
    this.addDecorCluster(CRYSTAL_VARIANTS, 0.11, 800, floor2, [
      { dx: -28, v: 1, s: 0 },
      { dx: 0, v: 0, s: 2 },
      { dx: 26, v: 3, s: 1 },
    ]);
    this.addDecor(1050, floor2, 'decor-b2-stalagmite', 0.14);
    this.addDecor(1200, floor2, 'decor-b2-puddle', 0.10);
    this.addDecor(1380, floor2, 'decor-b2-bones', 0.11);
    this.addDecorCluster(MUSHROOM_VARIANTS, 0.09, 1620, floor2, [
      { dx: -25, v: 3, s: 1 },
      { dx: 0, v: 1, s: 2 },
      { dx: 24, v: 0, s: 0 },
    ]);
    this.addDecor(1880, floor2, 'decor-b2-crystal-pillar', 0.15);
    this.addDecorCluster(CRYSTAL_VARIANTS, 0.09, 2080, floor2, [
      { dx: -20, v: 2, s: 1 },
      { dx: 5, v: 0, s: 0 },
      { dx: 26, v: 3, s: 2 },
    ]);

    // Чамбер 2 — кристаллы и грибы на полу
    this.addDecorCluster(CRYSTAL_VARIANTS, 0.10, 2380, floor2, [
      { dx: -28, v: 0, s: 0 },
      { dx: 0, v: 2, s: 2 },
      { dx: 24, v: 1, s: 1 },
    ]);
    this.addDecor(2620, floor2, 'decor-b2-bones', 0.10);
    this.addDecor(2780, floor2, 'decor-b2-stalagmite', 0.13);
    this.addDecorCluster(MUSHROOM_VARIANTS, 0.10, 3050, floor2, [
      { dx: -28, v: 2, s: 1 },
      { dx: -4, v: 0, s: 0 },
      { dx: 22, v: 3, s: 2 },
      { dx: 48, v: 1, s: 1 },
    ]);
    this.addDecor(3340, floor2, 'decor-b2-puddle', 0.10);
    this.addDecor(3520, floor2, 'decor-b2-crystal-pillar', 0.13);

    // Чамбер 3 — финальная композиция
    this.addDecorCluster(CRYSTAL_VARIANTS, 0.11, 3870, floor2, [
      { dx: -30, v: 3, s: 2 },
      { dx: -2, v: 1, s: 1 },
      { dx: 26, v: 0, s: 0 },
      { dx: 52, v: 2, s: 1 },
    ]);
    this.addDecor(4110, floor2, 'decor-b2-stalagmite', 0.13);
    this.addDecorCluster(MUSHROOM_VARIANTS, 0.10, 4350, floor2, [
      { dx: -25, v: 1, s: 0 },
      { dx: 0, v: 3, s: 2 },
      { dx: 25, v: 2, s: 1 },
    ]);
    this.addDecor(4570, floor2, 'decor-b2-bones', 0.11);
    this.addDecor(4730, floor2, 'decor-b2-puddle', 0.10);
    this.addDecor(4940, floor2, 'decor-b2-crystal-pillar', 0.14);

    // Маленькие группы на платформах
    this.addDecorCluster(MUSHROOM_VARIANTS, 0.06, 800, 2060 - 16, [
      { dx: -16, v: 0, s: 0 },
      { dx: 4, v: 2, s: 1 },
    ]);
    this.addDecorCluster(CRYSTAL_VARIANTS, 0.06, 1700, 1920 - 16, [
      { dx: -14, v: 1, s: 1 },
      { dx: 8, v: 3, s: 0 },
    ]);
    this.addDecor(3000, 1640 - 16, 'decor-b2-puddle', 0.07);
    this.addDecor(3500, 1326 - 16, 'decor-b2-bones', 0.07);
    this.addDecorCluster(MUSHROOM_VARIANTS, 0.06, 4400, 1700 - 16, [
      { dx: -12, v: 2, s: 1 },
      { dx: 8, v: 0, s: 0 },
    ]);
    this.addDecorCluster(CRYSTAL_VARIANTS, 0.07, 4900, 2200 - 16, [
      { dx: -14, v: 0, s: 1 },
      { dx: 10, v: 2, s: 2 },
    ]);
  }

  private spawnBiome2Enemies() {
    // Crawler-жуки в каждом чамбере
    this.spawnEnemy(1000, 0, 600, 1400);
    this.spawnEnemy(2700, 0, 2400, 3500);
    this.spawnEnemy(4500, 0, 4000, 5000);

    // Floater в Чамбере 2 — Xonix 45° медленный, отскок от стен/платформ
    this.addFloater(2900, 1700, 64, -64);

    // Dropper над Чамбером 1
    this.addDropper(1100, 200, 600, 1700);

    // Hoppers
    this.addHopper(1500, 0, 800, 2100);
    this.addHopper(4500, 0, 3900, 5050);
  }

  // ------------------------------------------------------------------
  // ХЕЛПЕРЫ ОБЩИЕ
  // ------------------------------------------------------------------

  private spawnEnemy(x: number, y: number, patrolMin: number, patrolMax: number) {
    // y=0 как «магическое значение» = на полу. Иначе используем переданное y.
    const finalY = y === 0 ? this.worldHeight - 50 : y;
    const e = new Enemy(this, x, finalY, patrolMin, patrolMax);
    e.setDepth(10);
    this.enemyGroup.add(e);
    this.physics.add.collider(e, this.terrain);
    this.enemies.push(e);
  }

  private handleTeleport(tp: Teleport) {
    if (this.isTeleporting) return;
    this.isTeleporting = true;
    // Сохраняем что игрок теперь в новом биоме (точка респауна обновится при
    // первом отдыхе у портала там).
    this.cameras.main.fade(420, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      // Сохраняем minimal — биом + дефолтный респаун (его пересчитает буилд биома)
      SaveSystem.save({
        biomeId: tp.target,
        respawnX: 0, // будет переопределено порталом при загрузке
        respawnY: 0,
        hp: this.player.hp,
        soul: this.player.soul,
        collectedArtefacts: Array.from(this.collectedArtefacts),
      });
      this.scene.restart({ biomeId: tp.target });
    });
  }

  // ------------------------------------------------------------------
  // ОТДЫХ У ПОРТАЛА
  // ------------------------------------------------------------------

  private handlePortalRest(delta: number) {
    if (this.player.isFocusing || this.player.isDashing) {
      this.portalRestTimer = 0;
      return;
    }

    const pBody = this.player.body as Phaser.Physics.Arcade.Body;
    const onGround = pBody.blocked.down || pBody.touching.down;

    const nearPortal =
      onGround &&
      Math.abs(this.player.x - this.portal.x) < this.portal.getInteractRadius();
    const idle =
      !this.inputState.left &&
      !this.inputState.right &&
      Math.abs(pBody.velocity.x) < 5;

    if (nearPortal && idle) {
      this.portalRestTimer += delta;
      if (this.portalRestTimer >= PORTAL_CONFIG.restDelay && !this.portalUsedThisVisit) {
        this.player.fullRestore();
        const respawn = this.portal.getRespawnPoint();
        this.player.setRespawnPoint(respawn.x, respawn.y);
        SaveSystem.save({
          biomeId: this.biomeId,
          respawnX: respawn.x,
          respawnY: respawn.y,
          hp: this.player.hp,
          soul: this.player.soul,
          collectedArtefacts: Array.from(this.collectedArtefacts),
        });
        this.cameras.main.flash(220, 230, 230, 255);
        this.portal.flashEmerge();
        this.portalUsedThisVisit = true;
      }
    } else {
      this.portalRestTimer = 0;
      this.portalUsedThisVisit = false;
    }
  }

  // ------------------------------------------------------------------
  // HUD
  // ------------------------------------------------------------------

  private updateHUD() {
    const g = this.hudLayer;
    g.clear();

    for (let i = 0; i < this.player.maxHP; i++) {
      const filled = i < this.player.hp;
      const x = 30 + i * 26;
      const y = 30;
      const a = filled ? 1 : 0.55;
      const skull = filled ? 0xeef2ff : 0x4a4d58;
      const dark = filled ? 0x141a22 : 0x202028;

      g.fillStyle(dark, a);
      g.fillTriangle(x - 6, y - 6, x - 9, y - 11, x - 3, y - 8);
      g.fillTriangle(x + 6, y - 6, x + 9, y - 11, x + 3, y - 8);

      g.fillStyle(skull, a);
      g.fillEllipse(x, y, 16, 18);

      g.lineStyle(1, dark, a * 0.85);
      g.strokeEllipse(x, y, 16, 18);

      g.fillStyle(dark, a);
      g.fillEllipse(x - 3, y - 1, 3, 4);
      g.fillEllipse(x + 3, y - 1, 3, 4);

      g.fillStyle(dark, a * 0.6);
      g.fillCircle(x, y + 4, 0.8);
    }

    const sx = 24;
    const sy = 52;
    const sw = 160;
    const sh = 10;
    g.fillStyle(0x182030, 0.85);
    g.fillRect(sx, sy, sw, sh);
    if (this.player.soul > 0) {
      const fillW = (this.player.soul / this.player.maxSoul) * (sw - 2);
      g.fillStyle(0x66bbee, 1);
      g.fillRect(sx + 1, sy + 1, fillW, sh - 2);
    }
    g.lineStyle(1, 0xffffff, 0.6);
    g.strokeRect(sx, sy, sw, sh);
  }

  // ------------------------------------------------------------------
  // АРТЕФАКТЫ
  // ------------------------------------------------------------------

  private setupArtefactHUDSlots(canvasW: number) {
    // 4 ячейки в правом-верхнем углу. Каждая 36×36 с тёмной рамкой.
    // Иконка артефакта (Image) видима только если артефакт собран.
    const slotSize = 36;
    const gap = 6;
    const totalW = ALL_ARTEFACTS.length * slotSize + (ALL_ARTEFACTS.length - 1) * gap;
    const startX = canvasW - totalW - 16;
    const slotY = 16;

    // Тёмные рамки — рисуются один раз через отдельный Graphics (не очищается per-frame)
    const frame = this.add.graphics().setScrollFactor(0).setDepth(2000);
    for (let i = 0; i < ALL_ARTEFACTS.length; i++) {
      const id = ALL_ARTEFACTS[i];
      const sx = startX + i * (slotSize + gap);
      // Подложка — темная плита
      frame.fillStyle(0x0a0d12, 0.85);
      frame.fillRect(sx, slotY, slotSize, slotSize);
      // Бронзовый кант (как у портала/карты)
      frame.lineStyle(2, 0x6a4a28, 0.85);
      frame.strokeRect(sx, slotY, slotSize, slotSize);
      // Внутренний тёмный кант
      frame.lineStyle(1, 0x000000, 1);
      frame.strokeRect(sx + 2, slotY + 2, slotSize - 4, slotSize - 4);

      // Иконка — невидима пока артефакт не собран
      const icon = this.add
        .image(sx + slotSize / 2, slotY + slotSize / 2, `artefact-${id}`)
        .setScrollFactor(0)
        .setDepth(2001);
      icon.setVisible(this.collectedArtefacts.has(id));
      this.artefactSlotIcons.set(id, icon);
    }
  }

  private placeBiome1Artefacts() {
    // a1 на ar3 (4000, 1900) — площадка артефакта, top y=1884.
    // Артефакт парит чуть выше площадки.
    if (!this.collectedArtefacts.has('a1')) {
      const a1 = new Artefact(this, 4000, 1858, 'a1');
      a1.setDepth(9);
      this.artefacts.push(a1);
    }
    // a2 на br3 (9700, 280) — top y=264
    if (!this.collectedArtefacts.has('a2')) {
      const a2 = new Artefact(this, 9700, 238, 'a2');
      a2.setDepth(9);
      this.artefacts.push(a2);
    }
  }

  private placeBiome2Artefacts() {
    // a3 на sa2 (2900, 940) — top y=924
    if (!this.collectedArtefacts.has('a3')) {
      const a3 = new Artefact(this, 2900, 898, 'a3');
      a3.setDepth(9);
      this.artefacts.push(a3);
    }
    // a4 на sb2 (4050, 1040) — top y=1024
    if (!this.collectedArtefacts.has('a4')) {
      const a4 = new Artefact(this, 4050, 998, 'a4');
      a4.setDepth(9);
      this.artefacts.push(a4);
    }
  }

  private handleArtefactPickup(a: Artefact) {
    if (!a.pickup()) return;
    this.collectedArtefacts.add(a.id);
    // Обновляем HUD-иконку
    const icon = this.artefactSlotIcons.get(a.id);
    if (icon) icon.setVisible(true);
    // Сохраняем в save
    const respawn = this.portal.getRespawnPoint();
    SaveSystem.save({
      biomeId: this.biomeId,
      respawnX: respawn.x,
      respawnY: respawn.y,
      hp: this.player.hp,
      soul: this.player.soul,
      collectedArtefacts: Array.from(this.collectedArtefacts),
    });
  }

  // ------------------------------------------------------------------
  // СПАВН НОВЫХ ВРАГОВ
  // ------------------------------------------------------------------

  private addFloater(
    x: number,
    y: number,
    vx = 64,
    vy = 64,
    bounds?: { left: number; right: number; top: number; bottom: number },
  ) {
    const f = new EnemyFloater(this, x, y, vx, vy, bounds);
    f.setDepth(10);
    this.enemyGroup.add(f);
    // Коллайдер с террейном — отскок от стен и платформ (setBounce(1,1) на body)
    this.physics.add.collider(f, this.terrain);
    this.enemies.push(f);
  }

  private addDropper(x: number, y: number, patrolMin: number, patrolMax: number) {
    const d = new EnemyDropper(this, x, y, patrolMin, patrolMax);
    d.setDepth(10);
    this.enemyGroup.add(d);
    // Не сталкивается с террейном — летает свободно под потолком
    this.enemies.push(d);
  }

  /**
   * Размещает декоративный объект на 4-м плане (depth=5 + layer*0.001).
   * Параметр layer — для редактора bring-forward / send-backward. Объекты
   * могут наслаиваться друг на друга — нет физики, нет коллизий.
   */
  addDecor(x: number, y: number, key: string, scale = 1, layer = 0) {
    return new DecorItem(this, x, y, key, scale, layer);
  }

  /**
   * Полянка из вариантов одного типа объекта с размерами 0.85/1.0/1.15.
   * Items описывают композицию: локальный offsetX от centerX, индекс варианта,
   * индекс размера (0..2), и опционально layer (для контроля кто над кем).
   * По умолчанию layer = индекс в массиве — позже размещённые поверх.
   */
  private addDecorCluster(
    variants: string[],
    baseScale: number,
    centerX: number,
    y: number,
    items: Array<{ dx: number; v: number; s: number; l?: number }>,
  ) {
    const sizeMul = [0.85, 1.0, 1.15];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const key = variants[it.v % variants.length];
      const scale = baseScale * sizeMul[it.s % 3];
      const layer = it.l !== undefined ? it.l : i;
      this.addDecor(centerX + it.dx, y, key, scale, layer);
    }
  }

  private addHopper(x: number, y: number, patrolMin: number, patrolMax: number) {
    const finalY = y === 0 ? this.worldHeight - 80 : y;
    const h = new EnemyHopper(this, x, finalY, patrolMin, patrolMax);
    h.setDepth(10);
    this.enemyGroup.add(h);
    this.physics.add.collider(h, this.terrain);
    this.enemies.push(h);
    this.hoppers.push(h);
  }
}
